import { m } from "@/paraglide/messages.js";

const ENTERPRISE_BADGE_TONES = {
  enterprise:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200",
  standard:
    "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-300",
} as const;

function normalizeEnterpriseValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return value > 0 ? "true" : "false";
  if (typeof value === "string") {
    return value.trim().toLowerCase();
  }
  return "";
}

export function isEnterpriseCustomer(value: unknown): boolean {
  const normalized = normalizeEnterpriseValue(value);
  return (
    normalized === "true" ||
    normalized === "enterprise" ||
    normalized === "1" ||
    normalized === "yes"
  );
}

export function getEnterpriseBadgeClass(value: unknown): string {
  return isEnterpriseCustomer(value)
    ? ENTERPRISE_BADGE_TONES.enterprise
    : ENTERPRISE_BADGE_TONES.standard;
}

export function getEnterpriseBadgeLabel(value: unknown): string {
  return isEnterpriseCustomer(value)
    ? m.customer_type_enterprise()
    : m.customer_type_standard();
}

const ORDER_SEQUENCE_TONES = {
  first:
    "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200",
  second:
    "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200",
  third:
    "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200",
  milestone:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200",
} as const;

const MILESTONE_ORDERS = new Set([50, 100, 200, 300, 400, 500, 1000]);

export function isDisplayableSequence(sequence: number | null): boolean {
  if (sequence === null) return false;
  return sequence <= 3 || MILESTONE_ORDERS.has(sequence);
}

export function getOrderSequenceBadgeClass(sequence: number | null): string {
  if (sequence === null) return "";
  if (sequence === 1) return ORDER_SEQUENCE_TONES.first;
  if (sequence === 2) return ORDER_SEQUENCE_TONES.second;
  if (sequence === 3) return ORDER_SEQUENCE_TONES.third;
  if (MILESTONE_ORDERS.has(sequence)) return ORDER_SEQUENCE_TONES.milestone;
  return "";
}

function getOrdinalSuffix(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  const mod10 = n % 10;
  if (mod10 === 1) return "st";
  if (mod10 === 2) return "nd";
  if (mod10 === 3) return "rd";
  return "th";
}

export function getOrderSequenceLabel(sequence: number | null): string {
  if (sequence === null) return "";
  return `${sequence}${getOrdinalSuffix(sequence)} Order`;
}
